import { REQUEST_STATUS } from "@src/constants";
import {
  Allocation,
  AllocationRowData,
  RoleRequest,
  WorkloadTableRowData,
} from "@src/types/role_request_types";
import { buildMapFromAllocationList } from "@src/utils/edit_allocation_utils";
import dayjs from "dayjs";
import { AdditionalInformation } from "@src/types";
import _ from "lodash";

/**
 ** Maps the whole request to a data format that the ag grid table can consume.
 *  - **First row** *Requested Workload*: compare requested allocation to child allocation (if a child exists otherwise just take the requested allocation)
 *  - **Second row** *Proposed* (if request has proposed values): compare the requested allocation to the proposed allocation
 * Both rows handle the data in the same way: the allocations have a percentage and an updatedPercentage property.
 * They store the initial value (percentage) and the updated value (updatedPercentage).
 *
 * This is used on project lead side.
 *
 * @param {RoleRequest} request the request that will be mapped to ag grid row data
 * @returns the mapped ag grid row data
 */
export function mapWorkloadTableRowData(
  request: RoleRequest
): WorkloadTableRowData[] {
  if (!request.allocationDetails || !request.allocationDetails.length)
    return [];

  const rowData: WorkloadTableRowData[] = [];

  // build first row
  const requestedAllocationMap: Map<string, AllocationRowData> =
    buildMapFromAllocationList(request.allocationDetails, "requiredPercentage");

  rowData.push({
    label:
      request.status === REQUEST_STATUS.CONFIRMED
        ? "Confirmed"
        : "Requested Workload",
    allocations: requestedAllocationMap,
  });

  // build second row if needed
  const isDeclinedWithDifferentWorkload = checkForDeclinedWithDifferentWorkload(
    request.status,
    request.allocationDetails
  );

  if (
    request.status === REQUEST_STATUS.PROPOSED ||
    isDeclinedWithDifferentWorkload
  ) {
    const proposedAllocationMap = buildMapFromProposedAllocationList(
      request.allocationDetails
    );

    rowData.push({
      label: "Proposed",
      proposedName: request.assignedEmployee?.fullName,
      allocations: proposedAllocationMap,
    });
  }

  return rowData;
}

/**
 * Checks if status is declined with different workload
 *
 * @param {string} status status of the request
 * @param {Allocation[]} allocations allocations of the request
 * @returns true if it's declined with different workload, false if not
 */
function checkForDeclinedWithDifferentWorkload(
  status: string,
  allocations: Allocation[]
): boolean {
  if (status !== REQUEST_STATUS.DECLINED) return false;

  // filter for all proposed allocation that are not empty
  const emptyProposedAllocation: Allocation[] = allocations.filter(
    (allocation: Allocation) => allocation.proposedPercentage == null
  );

  // if the filtered list has the same length as the original list then there are no proposed allocation
  return emptyProposedAllocation.length !== allocations.length;
}

/**
 * Maps the request allocations into a map (key = colId / "M-YYYY", value = allocationRowData object)
 *
 * @param {Allocation[]} allocations allocations of the request
 * @returns the new Map
 */
export function buildMapFromProposedAllocationList(
  allocations: Allocation[]
): Map<string, AllocationRowData> {
  const allocationMap = new Map<string, AllocationRowData>();

  for (const allocation of allocations) {
    const key = dayjs(allocation.date).format("M-YYYY");

    const allocationDetails: AllocationRowData = {
      percentage: allocation.requiredPercentage,
      updatedPercentage: allocation.proposedPercentage || undefined,
    };
    allocationMap.set(key, allocationDetails);
  }

  return allocationMap;
}

/**
 * Checks if the additional information is updated and enables the button if this happened.
 *
 * @returns boolean indicating if the additional information is updated
 */
export function isAdditionalInformationUpdated(
  additionalInformation: AdditionalInformation | undefined,
  request: RoleRequest | undefined
): boolean {
  if (!additionalInformation) return false;

  const initialValues: AdditionalInformation = {
    scope: request?.scope || "",
    projectIdentifier: request?.projectIdentifier || "",
    specificRole: request?.specificRole || "",
  };

  const normalized: AdditionalInformation = {
    scope: additionalInformation.scope || "",
    projectIdentifier: additionalInformation.projectIdentifier || "",
    specificRole: additionalInformation.specificRole || "",
  };

  return !_.isEqual(initialValues, normalized);
}
